import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { Router } from "@angular/router";
import * as Sentry from "@sentry/angular";

import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";

import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { AuthInterceptor } from "./auth/auth-interceptor";
import { ErrorInterceptor } from "./error-interceptor";
import { AngularMaterialModule } from "./angular-material.module";

import { ChooseBranchComponent } from "./main-section/choose-branch/choose-branch.component"
import { ChoosePrinterComponent } from "./main-section/choose-printer/choose-printer.component"
import { MainNavComponent } from './main-nav/main-nav.component';
import { NavigateDropdownComponent } from "./main-section/settings-area/navigate-dropdown/navigate-dropdown.component";
import { NavigationToBranchesComponent } from "./main-section/settings-area/navigate-dropdown/navigation-to-branches/navigation-to-branches.component";
import { QrLinkComponent } from "./main-section/qr-link/qr-link.component"

import { BidiModule } from "@angular/cdk/bidi";

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { PreloginComponent } from "./auth/prelogin/prelogin.component";
import { OtpCodeInputComponent } from "./auth/otp-code-input/otp-code-input.component";
import { SocialComponent } from "./auth/social/social.component";
import { TAndCComponent } from "./main-section/legal/t-and-c/t-and-c.component";
import { PrivacyPolicyComponent } from "./main-section/legal/privacy-policy/privacy-policy.component";
import { ReceiptViewComponent } from "./other-pages/receipt-view/receipt-view.component";

import { BranchesModule } from "./super-management/branch/branches.module";
import { ProductsModule } from "./super-management/product/products.module";
import { UsersModule } from "./super-management/user/users.module";
import { PapersModule } from "./super-management/paper/papers.module";

import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";

import { RightPlaceComponent } from "./dialog/right-place/right-place.component";
import { ImagePreviewComponent } from "./dialog/image-preview/image-preview.component";
import { ApplyToAllComponent } from "./dialog/apply-to-all/apply-to-all.component";
import { ResizeComponent } from "./dialog/resize/resize.component";
import { OrderSummaryComponent } from "./dialog/order-summary/order-summary.component";
import { DeleteOrderComponent } from "./dialog/delete-order/delete-order.component";
import { DeleteUserComponent } from "./dialog/delete-user/delete-user.component";
import { AddPointsComponent } from "./dialog/add-points/add-points.component";
import { CloseBranchComponent } from "./dialog/close-branch/close-branch.component";
import { PrintingTableComponent } from "./main-section/printing-table/printing-table.component";
import { MyOrdersComponent } from "./other-pages/my-orders/my-orders.component";
import { HomeComponent } from "./other-pages/home/home.component";
import { MyProfileComponent } from "./other-pages/my-profile/my-profile.component";
import { PrintingQueComponent } from "./other-pages/printing-que/printing-que.component";
import { QAndAComponent } from "./other-pages/q-and-a/q-and-a.component";
import { PhoneComponent } from "./dialog/phone/phone.component";
import { CopyScanComponent } from "./dialog/scan-copy/scan-copy.component";
import { FixProductsComponent } from "./dialog/fix-products/fix-products.component";
import { PropertyExplainComponent } from "./dialog/property-explain/property-explain.component";
import { BorderExplainComponent } from "./dialog/border-explain/border-explain.component";
import { StationScreenComponent } from "./super-management/station-screen/station-screen.component";
import { PrinterNumberComponent } from "./dialog/printer-number/printer-number.component"
import { PendingOrderComponent } from "./other-pages/pending-order/pending-order.component";
import { HelpersComponent } from "./other-pages/helpers/helpers.component";
import { EditInstructionsComponent } from "./other-pages/edit-instructions/edit-instructions.component";
import { PersonalWallComponent } from "./wall/personal-wall/personal-wall.component";
import { EazixExhibitionComponent } from "./wall/eazix-exhibition/eazix-exhibition.component";
import { WallCardSocialComponent } from "./wall/wall-card-social/wall-card-social.component";
import { WallCommentClampDirective } from "./wall/wall-comment-clamp.directive";
import { WallLineOverflowDirective } from "./wall/wall-line-overflow.directive";
import { SuCloseBranchComponent } from "./dialog/su-close-branch/su-close-branch.component";
import { SuEditUserComponent } from "./dialog/su-edit-user/su-edit-user.component";
import { SuEditLockCodeComponent } from "./dialog/su-edit-lock-code/su-edit-lock-code.component";
import { SuFullInventoryComponent } from "./dialog/su-full-inventory/su-full-inventory.component";
import { SuNewOrderSupplierComponent } from "./dialog/su-new-order-supplier/su-new-order-supplier.component";
import { SuAllScreensComponent } from "./dialog/su-all-screens/su-all-screens.component";
import { ProcessingFileComponent } from "./dialog/processing-file/processing-file.component";
import { LanguageChangeComponent } from "./dialog/language-change/language-change.component";
import { SuMessageToCustomerComponent } from "./dialog/su-message-to-customer/su-message-to-customer.component";
import { MessageToCustomerViewComponent } from "./dialog/message-to-customer-view/message-to-customer-view.component";
import { TransferFilesToUserComponent } from "./dialog/transfer-files-to-user/transfer-files-to-user.component";
import { PhPrintExplanationComponent } from "./dialog/ph-3d-print-explanation/ph-3d-print-explanation.component";
import { FileIssuesComponent } from "./dialog/file-issues/file-issues.component";
import { SuHelpersListComponent } from "./dialog/su-helpers-list/su-helpers-list.component";
import { CreatePriceQuoteComponent } from "./dialog/create-price-quote/create-price-quote.component";
import { CreditOrderComponent } from "./dialog/credit-order/credit-order.component";
import { PrintNodeStatusDialogComponent } from "./dialog/printnode-status-dialog/printnode-status-dialog.component";
import { BillingErrorsDialogComponent } from "./dialog/billing-errors-dialog/billing-errors-dialog.component";
import { PaymentRequestDialogComponent } from "./dialog/payment-request-dialog/payment-request-dialog.component";
import { ResendPartialFilesDialogComponent } from "./dialog/resend-partial-files/resend-partial-files-dialog.component";
import { InkGradientDialogComponent } from "./dialog/ink-gradient/ink-gradient-dialog.component";
import { WallContactDialogComponent } from "./dialog/wall-contact-dialog/wall-contact-dialog.component";
import { FileUploadModule } from 'ng2-file-upload';
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { PhLazySharedModule } from "./ph-lazy/ph-lazy-shared.module";

@NgModule({
  declarations: [
    AppComponent,
    MainNavComponent,
    ChooseBranchComponent,
    ChoosePrinterComponent,
    PrintingTableComponent,
    NavigateDropdownComponent,
    NavigationToBranchesComponent,
    QrLinkComponent,
    MyOrdersComponent,
    HomeComponent,
    MyProfileComponent,
    PrintingQueComponent,
    QAndAComponent,
    PreloginComponent,
    OtpCodeInputComponent,
    SocialComponent,
    TAndCComponent,
    PrivacyPolicyComponent,
    ReceiptViewComponent,
    RightPlaceComponent,
    PhoneComponent,
    CopyScanComponent,
    FixProductsComponent,
    SuCloseBranchComponent,
    PropertyExplainComponent,
    BorderExplainComponent,
    StationScreenComponent,
    ImagePreviewComponent,
    ApplyToAllComponent,
    ResizeComponent,
    OrderSummaryComponent,
    DeleteOrderComponent,
    DeleteUserComponent,
    AddPointsComponent,
    CloseBranchComponent,
    PrinterNumberComponent,
    PendingOrderComponent,
    HelpersComponent,
    EditInstructionsComponent,
    PersonalWallComponent,
    EazixExhibitionComponent,
    WallCardSocialComponent,
    WallCommentClampDirective,
    WallLineOverflowDirective,
    SuEditUserComponent,
    SuEditLockCodeComponent,
    SuFullInventoryComponent,
    SuNewOrderSupplierComponent,
    SuAllScreensComponent,
    ProcessingFileComponent,
    LanguageChangeComponent,
    SuMessageToCustomerComponent,
    MessageToCustomerViewComponent,
    TransferFilesToUserComponent,
    PhPrintExplanationComponent,
    FileIssuesComponent,
    SuHelpersListComponent,
    CreatePriceQuoteComponent,
    CreditOrderComponent,
    PrintNodeStatusDialogComponent,
    BillingErrorsDialogComponent,
    PaymentRequestDialogComponent,
    ResendPartialFilesDialogComponent,
    InkGradientDialogComponent,
    WallContactDialogComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AngularMaterialModule,
    BidiModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) =>
          new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${new Date().getTime()}`),
        deps: [HttpClient],
      },
    }),
    BranchesModule,
    ProductsModule,
    UsersModule,
    PapersModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FileUploadModule,
    MatProgressBarModule,
    PhLazySharedModule,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: 'Direction', useValue: 'ltr' }, // or 'rtl' depending on default direction
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    }, {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => { },
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
